import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions } from 'chart.js';
import { Subject, Subscription, interval, take, switchMap, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientAnalyticsAiSummaryStructured, ClientBrandTagResponse, ClientOverviewMovementCard, ClientOverviewRequest, ClientOverviewResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SentimentBadgeComponent, Sentiment } from '../../../../shared/components/sette/sentiment-badge/sentiment-badge.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, noMentionsLabel, isNewMetric, newMetricLabel } from '../shared/analytics-request.util';
import { translateTopic } from '../shared/taxonomy.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsDate, formatAnalyticsAxisDate, brandColor, Tone, SENTIMENT_COLORS, AXIS } from '../../../../shared/components/sette/chart-theme';

interface StoryItem {
	id: string;
	headline: string;
	brief?: string;
	outlet?: string;
	outletType?: string;
	dateLabel?: string;
	reach?: number;
	impact: number;
	brand?: string;
	sentiment: Sentiment;
	authors?: string;
	url?: string;
	topics: string[];
	// "Конкурент" badge - true only when every one of this clip's matching brand tags is
	// COMPETITOR (see ClientAnalyticsServiceImpl.buildMentionRows); false when it also carries
	// the client's own COMPANY tag, or when it matches no configured brand tag at all.
	competitorOnly: boolean;
}

type MovementBadgeType = 'improvement' | 'deterioration' | 'silence';

interface CompetitorItem {
	tagId: string;
	name: string;
	mentions: number;
	mentionsLabel: string;
	sov: number;
	sovLabel: string;
	isClient: boolean;
	colorHex: string;
}

interface DetailPage {
	id: string;
	label: string;
	desc: string;
	icon: string;
}

interface KpiView {
	value: string;
	trendUp?: boolean;
	deltaLabel: string;
	rangeText?: string;
}

// Structured AI summary (2026-11-01): the AI's text is never trusted as HTML - **bold** markers
// are split into plain segments here, and the template renders each with interpolation
// ({{ seg.text }}), which Angular escapes automatically. There is no path from the AI's output to
// innerHTML anywhere in this rendering.
interface AiSummarySegment {
	bold: boolean;
	text: string;
}

interface AiSummaryStoryView {
	direction: 'up' | 'down';
	segments: AiSummarySegment[];
}


// Ported from analytics-design/src/components/DashboardContent.tsx (the "Overview" screen), then
// rewired in the "connect analytics to real data" pass to call POST /v1/client/analytics/overview
// instead of using sample data. ZONE 3 ("Movements vs previous quarter") now reads real movement
// cards (per-brand mentions/SOV swings, topic-impact swings, medium silence detection) instead of
// sample data; cards that need a compare period simply don't appear without one. The "stories that
// mattered" topic badges are back, now showing every AI industry/corporate topic on the story
// (plural, since a clip can carry up to 4) instead of the design's single placeholder topic.
@Component({
	selector: 'app-overview',
	standalone: true,
	imports: [MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SentimentBadgeComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent],
	templateUrl: './overview.component.html',
	styleUrl: './overview.component.scss',
})
export class OverviewComponent implements OnChanges, OnDestroy {
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	overview: ClientOverviewResponse | null = null;

	// Фаза 4: skeleton only on a first load (no data to show yet); once something has loaded, a
	// refetch (filter change, retry) fades the existing content instead of hiding it.
	get showSkeleton(): boolean {
		return this.loading && !this.overview;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.overview;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.overview?.mentions?.value ?? 0) === 0;
	}

	refreshingAiSummary = false;
	aiSummaryErrorMessage: string | null = null;
	// AI summary stuck in "generating" forever (2026-11-01): true once the backend reports a real
	// failure (aiSummary.failed) OR this component's own poll gives up after AI_SUMMARY_POLL_TIMEOUT_MS
	// without a resolution either way - both render the exact same short "not available" message +
	// Retry button, so the user never sees a spinner that silently never finishes.
	aiSummaryFailed = false;
	private aiSummaryPollSub?: Subscription;
	private static readonly AI_SUMMARY_POLL_INTERVAL_MS = 5000;
	private static readonly AI_SUMMARY_POLL_TIMEOUT_MS = 90000;
	private lastOverviewRequest: ClientOverviewRequest | null = null;
	private readonly fetchTrigger = new Subject<ClientOverviewRequest>();

	selectedStory: StoryItem | null = null;

	// BOTTOM: Direct links to detail pages - pure navigation, no backend data involved. A getter
	// (not a readonly field) so it re-translates on a language switch, same reasoning as every
	// other translate.instant()-backed getter in this component.
	get detailPages(): DetailPage[] {
		const t = (key: string) => this.translate.instant(key);
		return [
			{ id: 'coverage', label: t('ANALYTICS_NAV.COVERAGE'), desc: t('ANALYTICS_OVERVIEW.DEEP_DIVE_DESC_COVERAGE'), icon: 'layers' },
			{ id: 'brands', label: t('ANALYTICS_NAV.BRANDS'), desc: t('ANALYTICS_OVERVIEW.DEEP_DIVE_DESC_BRANDS'), icon: 'sell' },
			{ id: 'topics', label: t('ANALYTICS_NAV.TOPICS'), desc: t('ANALYTICS_OVERVIEW.DEEP_DIVE_DESC_TOPICS'), icon: 'label' },
			{ id: 'sentiment', label: t('ANALYTICS_NAV.SENTIMENT'), desc: t('ANALYTICS_OVERVIEW.DEEP_DIVE_DESC_SENTIMENT'), icon: 'mood' },
			{ id: 'media', label: t('ANALYTICS_NAV.MEDIA'), desc: t('ANALYTICS_OVERVIEW.DEEP_DIVE_DESC_MEDIA'), icon: 'tv' },
		];
	}

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - a quick filter change cancels the
		// still-pending previous request (createAnalyticsFetchStream) instead of letting it resolve
		// later and overwrite the newer filter's data.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getOverviewAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.overview = result.data!;
					this.maybeStartAiSummaryPolling();
				} else {
					// Фаза 4: the stale response stays in `overview` (faded via isRefreshing in the
					// template) instead of being cleared - a failed refetch shouldn't blank a screen
					// that already had something to show.
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	ngOnDestroy(): void {
		this.stopAiSummaryPolling();
	}

	// Задача 7: locale for every number/date formatted on this screen - UtilsService is the
	// app-wide source of truth for the current UI language, kept in sync by the flag switcher
	// (UtilsService.languageManagement). Read fresh on every call (these all sit behind
	// template-bound getters), so switching language reformats immediately, no reload.
	get lang(): string {
		return this.utils.currentLanguage;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetchOverview();
		}
	}

	fetchOverview(): void {
		// Stop any in-flight AI-summary poll immediately on a real refetch (filter change, retry,
		// or the user's own "Retry" click below) - the upcoming response decides fresh whether
		// polling should (re)start, never two polls running at once.
		this.stopAiSummaryPolling();
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		const request: ClientOverviewRequest = fields;
		this.lastOverviewRequest = request;

		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(request);
	}

	// --- ZONE 0: AI summary ---

	// AI summary stuck in "generating" forever (2026-11-01): polling every 5s while generating is
	// true, stopping the moment the backend reports a real result (text or failed) or
	// AI_SUMMARY_POLL_TIMEOUT_MS elapses. Lightweight-status follow-up (2026-11-01): each tick now
	// calls GET /overview/ai-summary/status (filtersHash echoed back from the response that
	// started this poll) instead of re-running the whole POST /overview computation up to 18
	// times - that endpoint only ever reads the cache row + the in-memory regenerating/
	// lastFailureAt guards server-side, it never recomputes Overview and never schedules a new
	// generation itself (AnalyticsAiSummaryServiceImpl#getSummaryStatus). It still never bypasses
	// the backend's own 10-minute auto-retry throttle (docs/features/analitika) - a poll landing
	// inside that window just gets failed=true back immediately, no new AI call fired.
	private maybeStartAiSummaryPolling(): void {
		this.stopAiSummaryPolling();
		this.aiSummaryFailed = !!this.overview?.aiSummary?.failed;
		if (!this.overview?.aiSummary?.generating) {
			return;
		}
		const filtersHash = this.overview.aiSummary.filtersHash;
		if (!filtersHash) {
			// Nothing to poll with (should not happen while generating=true) - leave as "generating"
			// rather than guessing a status with no key to check.
			return;
		}

		// Counting ticks (not a Date.now() wall-clock deadline) - interval()'s own timer is the
		// only clock this needs, so it advances correctly under a test's virtual/fake timers too.
		const maxTicks = OverviewComponent.AI_SUMMARY_POLL_TIMEOUT_MS / OverviewComponent.AI_SUMMARY_POLL_INTERVAL_MS;
		this.aiSummaryPollSub = interval(OverviewComponent.AI_SUMMARY_POLL_INTERVAL_MS)
			.pipe(
				take(maxTicks),
				switchMap(() => this.api.getOverviewAiSummaryStatus(filtersHash).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			)
			.subscribe({
				next: (res) => {
					if (this.overview) {
						this.overview = { ...this.overview, aiSummary: res };
					}
					if (!res.generating) {
						this.aiSummaryFailed = !!res.failed;
						this.stopAiSummaryPolling();
					}
				},
				error: () => {
					this.aiSummaryFailed = true;
					this.stopAiSummaryPolling();
				},
				complete: () => {
					// take()'s tick budget ran out while still generating (no error, no resolution
					// either way) - treat exactly like a failure: short "not available" message +
					// Retry, never a forever spinner.
					if (this.overview?.aiSummary?.generating) {
						this.aiSummaryFailed = true;
						this.overview = { ...this.overview, aiSummary: { ...this.overview.aiSummary, generating: false } };
					}
				},
			});
	}

	private stopAiSummaryPolling(): void {
		this.aiSummaryPollSub?.unsubscribe();
		this.aiSummaryPollSub = undefined;
	}

	// "Обиди се повторно" under the failed/timed-out AI summary (2026-11-01 follow-up): a plain
	// refetch (fetchOverview -> getOrScheduleSummary) was wrong here - that path respects the
	// automatic 10-minute lastFailureAt throttle on the backend, so clicking Retry inside that
	// window just got failed=true back again and did nothing visible. The user explicitly asked
	// to retry, so this goes through the same synchronous refreshNow() the "Refresh" button
	// uses - refreshNow never checks lastFailureAt (only its own, separate
	// nextManualRefreshAllowedAt throttle, which canRefreshAiSummary/the template already
	// disable the button on), and a success there now clears lastFailureAt server-side too.
	retryAiSummary(): void {
		this.refreshAiSummary();
	}

	get aiSummaryGeneratedLabel(): string {
		const generatedAt = this.overview?.aiSummary?.generatedAt;
		if (!generatedAt) return '';
		const date = new Date(generatedAt);
		const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		return `${formatAnalyticsDate(date, this.lang)}, ${time}`;
	}

	get canRefreshAiSummary(): boolean {
		const nextAllowed = this.overview?.aiSummary?.nextManualRefreshAllowedAt;
		if (!nextAllowed) return true;
		return Date.now() >= new Date(nextAllowed).getTime();
	}

	// "Обиди се повторно по HH:mm" when the manual throttle (nextManualRefreshAllowedAt) is still
	// active - shown on the disabled Retry/Refresh button instead of silently doing nothing.
	get nextManualRefreshAllowedLabel(): string {
		const nextAllowed = this.overview?.aiSummary?.nextManualRefreshAllowedAt;
		if (!nextAllowed) return '';
		return new Date(nextAllowed).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	refreshAiSummary(): void {
		if (this.refreshingAiSummary || !this.canRefreshAiSummary || !this.lastOverviewRequest) {
			return;
		}
		this.refreshingAiSummary = true;
		this.aiSummaryErrorMessage = null;
		this.api
			.refreshOverviewAiSummary(this.lastOverviewRequest)
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					if (this.overview) {
						this.overview = { ...this.overview, aiSummary: res };
					}
					this.refreshingAiSummary = false;
				},
				error: () => {
					this.refreshingAiSummary = false;
					this.aiSummaryErrorMessage = this.translate.instant('ANALYTICS_OVERVIEW.AI_SUMMARY_ERROR');
				},
			});
	}

	// Structured AI summary (2026-11-01): `structured` is parsed server-side from the same cached
	// text `aiSummary.text` holds - null whenever the text isn't valid JSON (an older cached row,
	// or the model drifting from the prompt's JSON instruction), in which case the template falls
	// back to the plain paragraph exactly as before this change.
	get aiSummaryStructured(): ClientAnalyticsAiSummaryStructured | null {
		return this.overview?.aiSummary?.structured ?? null;
	}

	get hasStructuredAiSummary(): boolean {
		const s = this.aiSummaryStructured;
		if (!s) return false;
		return !!(s.headline || s.coverage || s.tone || s.competition || s.stories?.length);
	}

	get aiSummaryHeadlineSegments(): AiSummarySegment[] {
		return this.parseBoldSegments(this.aiSummaryStructured?.headline);
	}

	get aiSummaryCoverageSegments(): AiSummarySegment[] {
		return this.parseBoldSegments(this.aiSummaryStructured?.coverage);
	}

	get aiSummaryToneSegments(): AiSummarySegment[] {
		return this.parseBoldSegments(this.aiSummaryStructured?.tone);
	}

	get aiSummaryCompetitionSegments(): AiSummarySegment[] {
		return this.parseBoldSegments(this.aiSummaryStructured?.competition);
	}

	get aiSummaryStoryViews(): AiSummaryStoryView[] {
		return (this.aiSummaryStructured?.stories ?? [])
			.filter((s) => !!s.text)
			.map((s) => ({
				direction: s.direction === 'down' ? 'down' : 'up',
				segments: this.parseBoldSegments(s.text),
			}));
	}

	// Splits "...**bold**..." into alternating plain/bold segments - the ONLY markup this
	// renders out of the AI's text. Everything else stays plain text, shown via interpolation
	// (never innerHTML), so the AI can never inject real HTML.
	private parseBoldSegments(text?: string | null): AiSummarySegment[] {
		if (!text) return [];
		const segments: AiSummarySegment[] = [];
		const pattern = /\*\*(.+?)\*\*/g;
		let lastIndex = 0;
		let match: RegExpExecArray | null;
		while ((match = pattern.exec(text))) {
			if (match.index > lastIndex) {
				segments.push({ bold: false, text: text.slice(lastIndex, match.index) });
			}
			segments.push({ bold: true, text: match[1] });
			lastIndex = match.index + match[0].length;
		}
		if (lastIndex < text.length) {
			segments.push({ bold: false, text: text.slice(lastIndex) });
		}
		return segments;
	}

	// --- ZONE 1: Stories that mattered ---

	get sortedStories(): StoryItem[] {
		const stories = (this.overview?.topStories ?? []).map((s) => ({
			id: s.clipId!,
			headline: s.headline || '(no title)',
			brief: s.brief || undefined,
			outlet: s.outletName || undefined,
			outletType: s.outletType || undefined,
			dateLabel: s.publicationDate ? formatAnalyticsDate(new Date(s.publicationDate), this.lang) : undefined,
			reach: s.reach ?? undefined,
			impact: s.impact ?? 0,
			brand: s.brandTagName || undefined,
			sentiment: this.mapSentiment(s.sentiment),
			authors: s.authorNames?.length ? s.authorNames.join(', ') : undefined,
			url: s.url || undefined,
			topics: [...(s.industryTopics ?? []), ...(s.corporateTopics ?? [])],
			competitorOnly: s.competitorOnly ?? false,
		}));
		return stories.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
	}

	private mapSentiment(value?: string): Sentiment {
		if (value === 'POSITIVE') return 'Positive';
		if (value === 'NEGATIVE') return 'Negative';
		return 'Neutral';
	}

	// SETTE redesign (CLIPPING-598): Impact is a signed score, so Правило А applies - color by
	// sign via signTone (0 is neutral, not positive), and the minus is always U+2212.
	impactLabel(impact: number): string {
		return formatSignedAuto(impact, this.lang);
	}

	impactTone(impact: number): Tone {
		return signTone(impact);
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	toneTextClass(tone: Tone): string {
		if (tone === 'positive') return 'text-positive';
		if (tone === 'negative') return 'text-negative';
		return 'text-ink-subtle';
	}

	storyMeta(story: StoryItem): string {
		return [story.outlet, story.dateLabel].filter((p): p is string => !!p).join(' · ');
	}

	selectStory(story: StoryItem): void {
		this.selectedStory = story;
	}

	closeModal(): void {
		this.selectedStory = null;
	}

	// Пакет А #7: story.topics/selectedStory.topics carry the backend's English AiTaxonomy label
	// (same raw strings Topics/Media/Sentiment already run through translateTopic) - this screen
	// was rendering them as-is instead.
	translateStoryTopic(topic: string): string {
		return translateTopic(topic, this.translate);
	}

	// --- ZONE 2: KPIs ---

	get totalMentions(): number {
		return Math.round(this.overview?.mentions?.value ?? 0);
	}

	get totalMentionsLabel(): string {
		return formatAnalyticsInt(this.totalMentions, this.lang);
	}

	// Фикс 3: `mentions`/`totalMentions` above is the focus-brand-only KPI (backend's
	// focusKpis.mentions()), correct for the trend chart's own caption since the trend is built
	// from focus-only rows too. Stories That Mattered and the Competitor List badge both list
	// EVERY selected brand's mentions (backend's pool-wide `currentRows`), so their "N total"
	// badge must sum across `overview.brands` instead, or it shows the focus brand's smaller count
	// next to a list of rows drawn from the full pool.
	get poolMentionsLabel(): string {
		const total = (this.overview?.brands ?? []).reduce((sum, b) => sum + (b.mentions ?? 0), 0);
		return formatAnalyticsInt(total, this.lang);
	}

	get mentionsKpi(): KpiView {
		return this.buildKpi(this.overview?.mentions?.value, this.overview?.mentions?.previousValue, this.overview?.mentions?.hasEnoughCompareData, (n) => formatAnalyticsInt(Math.round(n), this.lang), '%');
	}

	// % positive minus % negative mentions for the client's own focus brand - same 10-mention
	// floor as the other averages, via ClientAnalyticsServiceImpl#buildNetSentimentKpi (reuses
	// computeNetSentiment, the same formula already backing the AI summary's Net Sentiment line).
	get netSentimentKpi(): KpiView {
		return this.buildKpi(this.overview?.netSentiment?.value, this.overview?.netSentiment?.previousValue, this.overview?.netSentiment?.hasEnoughCompareData, (n) => `${formatSigned(n, 1, this.lang)}%`, 'pts');
	}

	// Unlike every other KPI tile (Правило В: value always text-ink, color only on the trend
	// delta), Net Sentiment's own sign IS the finding - green/red on the value itself, same
	// same-sign convention the Brands screen already uses for netSentimentPercent (signTone).
	get netSentimentValueTone(): Tone {
		const v = this.overview?.netSentiment?.value;
		if (v == null) return 'neutral';
		return signTone(v);
	}

	get avgReachKpi(): KpiView {
		return this.buildKpi(this.overview?.avgReach?.value, this.overview?.avgReach?.previousValue, this.overview?.avgReach?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}/100`, 'pts');
	}

	get avgImpactKpi(): KpiView {
		return this.buildKpi(this.overview?.avgImpact?.value, this.overview?.avgImpact?.previousValue, this.overview?.avgImpact?.hasEnoughCompareData, (n) => (n >= 0 ? '+' : '-') + formatAnalyticsNumber(n, this.lang, 1), 'pts');
	}

	get shareOfVoiceKpi(): KpiView {
		return this.buildKpi(this.overview?.shareOfVoice?.value, this.overview?.shareOfVoice?.previousValue, this.overview?.shareOfVoice?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}%`, 'pp');
	}

	get shareOfVoiceSubLabel(): string {
		const brands = this.overview?.brands ?? [];
		if (!brands.length) return '';
		const others = Math.max(brands.length - 1, 0);
		const focus = this.focusBrandName;
		return focus ? `· ${focus} vs ${others} competitor${others === 1 ? '' : 's'}` : '';
	}

	get compareSubLabel(): string {
		return this.filters?.comparePeriod || '';
	}

	private get focusBrandId(): string | undefined {
		const selected = this.filters?.brands ?? [];
		if (selected.length === 1) return selected[0];
		const company = this.brandOptions.find((b) => b.brandKind === 'COMPANY');
		if (company?.tagId && selected.includes(company.tagId)) return company.tagId;
		return selected[0];
	}

	private get focusBrandName(): string | undefined {
		return this.brandOptions.find((b) => b.tagId === this.focusBrandId)?.name;
	}

	private buildKpi(
		value: number | undefined,
		previousValue: number | undefined | null,
		hasEnoughCompareData: boolean | undefined,
		format: (n: number) => string,
		deltaSuffix: 'pts' | '%' | 'pp'
	): KpiView {
		// Analytics accuracy review follow-up (2026-11-01): a number is never hidden for being small -
		// only a genuinely empty period (0 mentions, value null) has nothing to show at all.
		if (value == null) {
			return { value: '—', deltaLabel: noMentionsLabel(this.translate) };
		}
		const comparison = resolveComparison(isCompareRequested(this.filters), previousValue, hasEnoughCompareData);
		if (comparison.insufficient) {
			// No usable previous value at all (the compare period had 0 mentions) - "Ново" if this
			// period has a real value to report, otherwise nothing to say.
			return { value: format(value), deltaLabel: isNewMetric(value, 0) ? newMetricLabel(this.translate) : '' };
		}
		if (!comparison.show) {
			return { value: format(value), deltaLabel: '' };
		}
		const prev = comparison.previous as number;
		const delta = value - prev;
		let deltaLabel: string;
		if (deltaSuffix === '%' && isNewMetric(value, prev)) {
			deltaLabel = newMetricLabel(this.translate);
		} else if (deltaSuffix === '%') {
			const pct = prev !== 0 ? Math.round((delta / prev) * 100) : 0;
			deltaLabel = `${pct >= 0 ? '+' : '-'}${formatAnalyticsInt(Math.abs(pct), this.lang)}%`;
		} else {
			deltaLabel = `${delta >= 0 ? '+' : '-'}${formatAnalyticsNumber(Math.abs(delta), this.lang, 1)} ${deltaSuffix}`;
		}
		return {
			value: format(value),
			trendUp: value >= prev,
			deltaLabel,
			rangeText: `(${format(prev)} → ${format(value)})`,
		};
	}

	// --- ZONE 2: Trend chart ---

	get isWeeklyBucketing(): boolean {
		if (!this.filters?.startDate || !this.filters?.endDate) return false;
		const days = Math.round((this.filters.endDate.getTime() - this.filters.startDate.getTime()) / 86400000);
		return days > 60;
	}

	// Returns an i18n KEY (resolved by the template's own `| translate`), not the literal word -
	// same reasoning as every other language-dependent getter here.
	get trendBucketUnit(): string {
		return this.isWeeklyBucketing ? 'ANALYTICS_OVERVIEW.UNIT_WEEKS' : 'ANALYTICS_OVERVIEW.UNIT_DAYS';
	}

	get bucketCount(): number {
		return this.overview?.trend?.length ?? 0;
	}

	get avgMentionsPerBucket(): string {
		return formatAnalyticsNumber(this.bucketCount ? this.totalMentions / this.bucketCount : 0, this.lang, 1);
	}

	// SETTE redesign (CLIPPING-598): Mentions (volume, no sign) and Avg Visibility (0-100, no
	// sign) each get their own color - neither is green, since neither carries "good/bad" meaning
	// on its own (Правило А). Impact IS signed, so Правило А applies literally: the line itself is
	// blue, and the sign lives on each point via signTone, not on the line's color.
	// Trend colors follow-up (2026-11-01): Mentions bars and the Impact line were both the same
	// dark blue (#244896, bars just at lower opacity) and hard to tell apart at a glance; Mentions
	// is now a neutral light gray (it's a volume count, not a scored metric), Visibility is orange,
	// Impact stays dark blue - three visually distinct colors, matching the legend below.
	get trendChartData(): ChartData {
		const trend = this.overview?.trend ?? [];
		return {
			labels: trend.map((t) => formatAnalyticsAxisDate(new Date(t.bucketStart!), this.lang)),
			datasets: [
				{
					type: 'bar',
					label: this.translate.instant('ANALYTICS_GLOSSARY.MENTIONS'),
					data: trend.map((t) => t.mentions ?? 0),
					backgroundColor: '#D7DBE3',
					borderRadius: 3,
					barThickness: 18,
					yAxisID: 'volume',
				},
				{
					type: 'line',
					label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX'),
					data: trend.map((t) => t.avgReach ?? null),
					borderColor: '#F59E0B',
					backgroundColor: '#F59E0B',
					borderWidth: 2.2,
					pointRadius: 3,
					pointBackgroundColor: '#F59E0B',
					tension: 0.4,
					yAxisID: 'reach',
					spanGaps: true,
				},
				{
					type: 'line',
					label: this.translate.instant('ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE'),
					data: trend.map((t) => t.avgImpact ?? null),
					borderColor: '#244896',
					backgroundColor: '#244896',
					borderWidth: 2.2,
					borderDash: [4, 3],
					pointRadius: 3.5,
					pointBackgroundColor: trend.map((t) => SENTIMENT_COLORS[signTone(t.avgImpact ?? 0)]),
					tension: 0.4,
					yAxisID: 'impact',
					spanGaps: true,
				},
			],
		};
	}

	get trendChartOptions(): ChartOptions {
		const maxMentions = Math.max(1, ...(this.overview?.trend ?? []).map((t) => t.mentions ?? 0));
		return {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: {
				legend: { display: false },
				tooltip: {
					backgroundColor: AXIS.tooltipBg,
					titleColor: '#ffffff',
					bodyColor: '#ffffff',
					borderWidth: 0,
					padding: 10,
					cornerRadius: 10,
					displayColors: true,
					callbacks: {
						// datasetIndex, not dataset.label, decides formatting - the label itself is
						// already translated text by the time Chart.js calls this, so it can't be
						// compared against a literal English string.
						label: (ctx) => {
							const v = ctx.parsed.y;
							if (ctx.datasetIndex === 2) return `${ctx.dataset.label}: ${formatSignedAuto(Math.round(v as number), this.lang)}`;
							if (ctx.datasetIndex === 1) return `${ctx.dataset.label}: ${formatAnalyticsNumber(v as number, this.lang, 1)}/100`;
							return `${ctx.dataset.label}: ${formatAnalyticsInt(v as number, this.lang)}`;
						},
					},
				},
			},
			scales: {
				x: {
					grid: { color: AXIS.grid },
					ticks: { color: AXIS.text, font: { size: 10 } },
				},
				impact: {
					type: 'linear',
					position: 'left',
					min: -100,
					max: 100,
					ticks: {
						stepSize: 50,
						color: AXIS.text,
						font: { size: 10 },
						callback: (v) => formatSignedAuto(Number(v), this.lang),
					},
					grid: { color: AXIS.grid },
				},
				reach: {
					type: 'linear',
					position: 'right',
					min: 0,
					max: 100,
					ticks: { stepSize: 25, color: AXIS.text, font: { size: 10 } },
					grid: { drawOnChartArea: false },
				},
				volume: {
					type: 'linear',
					position: 'right',
					min: 0,
					max: Math.ceil(maxMentions * 1.6),
					display: false,
				},
			},
		};
	}

	// --- ZONE 2: Competitor list ---

	get competitorList(): CompetitorItem[] {
		return (this.overview?.brands ?? []).map((b) => ({
			tagId: b.tagId!,
			name: b.name!,
			mentions: b.mentions ?? 0,
			mentionsLabel: formatAnalyticsInt(b.mentions ?? 0, this.lang),
			sov: b.sharePercent ?? 0,
			sovLabel: `${formatAnalyticsNumber(b.sharePercent ?? 0, this.lang, 1)}%`,
			isClient: b.brandKind === 'COMPANY',
			colorHex: brandColor(b.tagId, this.brandOptions),
		}));
	}

	get maxSov(): number {
		return Math.max(1, ...this.competitorList.map((b) => b.sov));
	}

	// --- ZONE 3: Movements ---

	get movements(): ClientOverviewMovementCard[] {
		return this.overview?.movements ?? [];
	}

	get hasComparePeriod(): boolean {
		return this.filters?.compareType !== 'none' && !!this.filters?.compareStartDate && !!this.filters?.compareEndDate;
	}

	movementBadgeType(card: ClientOverviewMovementCard): MovementBadgeType {
		if (card.type === 'silence') return 'silence';
		if (card.type === 'mentions_down') return 'deterioration';
		if (card.type === 'mentions_up') return 'improvement';
		return (card.badge || '').trim().startsWith('-') ? 'deterioration' : 'improvement';
	}

	movementBadgeClasses(type: MovementBadgeType): string {
		if (type === 'improvement') return 'bg-positive-tint text-positive';
		if (type === 'deterioration') return 'bg-negative-tint text-negative';
		return 'bg-warning-tint text-warning';
	}

	onMovementClick(card: ClientOverviewMovementCard): void {
		if (card.targetTab) {
			this.navigate.emit(card.targetTab);
		}
	}

	// --- Navigation ---

	onDeepDiveClick(page: DetailPage): void {
		this.navigate.emit(page.id);
	}
}
